import React, { useCallback, useEffect, useRef, useState } from "react";
// @ts-ignore — ported vanilla Three.js module (see src/serga-os/)
import { boot } from "../serga-os/main.js";
// @ts-ignore — ported vanilla data module
import { setLiveProjects, setLiveInvestments, setLiveBudget, setLiveFocus, setLiveStream, setLiveTravel, setLiveVitals, setLiveCapture, setLiveSystem } from "../serga-os/data.js";
import { mapProject } from "../serga-os/mapProjects";
import { mapTrip } from "../serga-os/mapTravel";
import { ProjectsService } from "../services/projectsService";
import { FinanceService } from "../services/financeService";
import { FeedService } from "../services/feedService";
import { TravelService } from "../services/travelService";
import { SpotifyService } from "../services/spotifyService";
import { ChangesService } from "../services/changesService";
import { VitalsService } from "../services/vitalsService";
import { CaptureService } from "../services/captureService";
import { SystemService } from "../services/systemService";
import { useAuth } from "../contexts/AuthContext";
import "../serga-os/styles.css";

/**
 * SergaOS — the custom Three.js "Personal OS" experience.
 *
 * The world boots locked and only the ambient field (starfield, core glow,
 * saucer fly-bys) runs behind a single auth card. The card is wired to the
 * existing Cognito flow via AuthContext; on a valid session the constellation
 * blooms in (world.unlock()) and live projects are loaded. LOCK signs out and
 * returns to the gate. Editing still happens via the MCP server / SHELL.
 */
const SergaOS: React.FC = () => {
  const { tokens, user, isAuthenticated, isLoading, error, signIn, signOut, clearError } =
    useAuth();
  const worldRef = useRef<any>(null);
  const trayRef = useRef<any>(null);
  const sonicRef = useRef<any>(null);
  const liveLoadedRef = useRef(false);
  const tokensRef = useRef<any>(null);
  // Change-feed state: the cursor (last server `now`) and a cache of the live
  // project details by id, so a delta can re-map the full list with stable
  // glyph/tag indices (see pollChanges below).
  const changesCursorRef = useRef<string | null>(null);
  const projectDetailsRef = useRef<Map<string, any>>(new Map());
  const [creds, setCreds] = useState({ user: "", pass: "" });
  const [submitting, setSubmitting] = useState(false);
  // Spotify OAuth callback status (visible toast while exchanging the code).
  const [spotifyLink, setSpotifyLink] = useState<
    { state: "linking" | "linked" | "error"; msg: string } | null
  >(null);
  const spotifyLinkDoneRef = useRef(false);

  // Pull live data (projects + investments) and reconcile the world. Used both
  // for the initial reveal and as the SHELL's re-pull-on-failure callback. Each
  // source is best-effort: a failure keeps that source's current/fixture data.
  const reloadProjects = useCallback(async () => {
    const world = worldRef.current;
    if (!world) return;
    let changed = false;

    try {
      const list = await ProjectsService.listProjects("active");
      const details = await Promise.all(
        list.map((p) => ProjectsService.getProject(p.projectId)),
      );
      // Authoritative snapshot — refresh the details cache the change-feed
      // diffs against (drops projects that are no longer active/present).
      projectDetailsRef.current = new Map(details.map((d) => [d.projectId, d]));
      const mapped = details.map(mapProject);
      if (mapped.length) {
        setLiveProjects(mapped);
        changed = true;
      }
    } catch (e) {
      console.warn("[SergaOS] project reload failed; keeping current data", e);
    }

    try {
      const investments = await FinanceService.getInvestments();
      if (investments) {
        setLiveInvestments(investments);
        changed = true;
      }
    } catch (e) {
      console.warn("[SergaOS] investments reload failed; keeping fixture", e);
    }

    try {
      const budget = await FinanceService.getBudget();
      if (budget) {
        setLiveBudget(budget);
        changed = true;
      }
    } catch (e) {
      console.warn("[SergaOS] budget reload failed; keeping fixture", e);
    }

    try {
      const trips = await TravelService.listTrips();
      if (trips.length) {
        setLiveTravel(trips.map(mapTrip));
        changed = true;
      }
    } catch (e) {
      console.warn("[SergaOS] travel reload failed; keeping fixture", e);
    }

    try {
      const vitals = await VitalsService.getVitals();
      if (vitals) {
        setLiveVitals(vitals);
        changed = true;
      }
    } catch (e) {
      console.warn("[SergaOS] vitals reload failed; keeping fixture", e);
    }

    try {
      const capture = await CaptureService.getCapture();
      if (capture) {
        setLiveCapture(capture);
        changed = true;
      }
    } catch (e) {
      console.warn("[SergaOS] capture reload failed; keeping fixture", e);
    }

    try {
      const system = await SystemService.getSystem();
      if (system) {
        setLiveSystem(system);
        changed = true;
      }
    } catch (e) {
      console.warn("[SergaOS] system reload failed; keeping fixture", e);
    }

    if (changed) world.sync();
  }, []);

  // Pull the FEED ({ focus, stream }) and re-render the tray. Kept separate from
  // reloadProjects: feed changes never touch the 3D field (no world.sync), and
  // it's polled on its own cadence while the tray is open. Best-effort.
  const refreshFeed = useCallback(async () => {
    try {
      const feed = await FeedService.getFeed();
      if (feed) {
        setLiveFocus(feed.focus);
        setLiveStream(feed.stream);
        trayRef.current?.refresh();
      }
    } catch (e) {
      console.warn("[SergaOS] feed reload failed; keeping fixture", e);
    }
  }, []);

  // Delta poll: ask the change-feed "what changed since my last poll?" and apply
  // only the moved sources. Cheap (mostly empty responses) so it runs often, and
  // it re-syncs the 3D field ONLY when something visual actually changed — an
  // empty delta touches nothing. Cursorless until the handshake in the gate
  // effect sets changesCursorRef.
  const pollChanges = useCallback(async () => {
    const world = worldRef.current;
    const since = changesCursorRef.current;
    if (!world || !since) return;

    let resp;
    try {
      resp = await ChangesService.getChanges(since);
    } catch (e) {
      console.warn("[SergaOS] change poll failed; will retry next tick", e);
      return;
    }
    if (!resp) return;
    changesCursorRef.current = resp.now;

    const c = resp.changed || {};
    let visual = false;

    if (Array.isArray(c.projects) && c.projects.length) {
      for (const d of c.projects) projectDetailsRef.current.set(d.projectId, d);
      // Re-map the whole live set so glyph/tag indices stay consistent with a
      // full load. Mirrors listProjects("active") ordering (updatedAt desc).
      const merged = Array.from(projectDetailsRef.current.values())
        .filter((d) => d.status === "active")
        .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
      setLiveProjects(merged.map(mapProject));
      visual = true;
    }
    if (c.investments) {
      setLiveInvestments(c.investments);
      visual = true;
    }
    if (c.budget) {
      setLiveBudget(c.budget);
      visual = true;
    }
    if (c.travel && Array.isArray(c.travel.trips) && c.travel.trips.length) {
      setLiveTravel(c.travel.trips.map(mapTrip));
      visual = true;
    }
    if (c.vitals) {
      setLiveVitals(c.vitals);
      visual = true;
    }
    if (c.capture) {
      setLiveCapture(c.capture);
      visual = true;
    }
    if (c.system) {
      setLiveSystem(c.system);
      visual = true;
    }
    if (c.focus) {
      setLiveFocus(c.focus);
      trayRef.current?.refresh();
    }
    if (visual) world.sync();
  }, []);

  // Boot the 3D world once on mount. It starts locked; the gate effect below
  // reveals it once Cognito reports an authenticated session.
  useEffect(() => {
    document.body.classList.add("serga-os");
    const { cleanup, world, tray, sonic } = boot({
      sessionLabel: user?.email,
      authed: isAuthenticated,
      reloadProjects,
      // lets the CLOUD portal link the home node via silent Cognito SSO
      getCloudToken: () => tokensRef.current?.idToken || null,
    });
    worldRef.current = world;
    trayRef.current = tray;
    sonicRef.current = sonic;

    return () => {
      cleanup();
      worldRef.current = null;
      trayRef.current = null;
      sonicRef.current = null;
      liveLoadedRef.current = false;
      document.body.classList.remove(
        "serga-os",
        "locked",
        "lvl0",
        "lvl1",
        "lvl2",
        "focused",
      );
    };
    // boot once; auth transitions are handled by the effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Spotify OAuth callback — runs on its own, independent of the world-boot /
  // auth gate so it can never be silently skipped. Lands at /spotify/callback?code=…;
  // it waits for the Cognito session, exchanges the code server-side, shows a
  // visible status, then cleans the URL back to /.
  useEffect(() => {
    // Read the code captured by the inline <head> script before Amplify could
    // strip it; fall back to the live URL if the stash is somehow absent.
    const stash = (window as any).__SPOTIFY_OAUTH__ as
      | { code: string | null; error: string | null }
      | undefined;
    const onCallback =
      !!stash || window.location.pathname === "/spotify/callback";
    if (!onCallback) return;
    if (spotifyLinkDoneRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const code = stash ? stash.code : params.get("code");
    const err = stash ? stash.error : params.get("error");
    if (err) {
      spotifyLinkDoneRef.current = true;
      setSpotifyLink({ state: "error", msg: `Spotify declined: ${err}` });
      window.history.replaceState({}, "", "/");
      return;
    }
    if (!code) {
      spotifyLinkDoneRef.current = true;
      if (window.location.pathname === "/spotify/callback") {
        window.history.replaceState({}, "", "/");
      }
      return;
    }
    // Wait for the Cognito session to restore before calling the authed endpoint;
    // this effect re-runs when tokens arrive.
    if (!isAuthenticated || !tokens) {
      setSpotifyLink({ state: "linking", msg: "Restoring session…" });
      return;
    }
    spotifyLinkDoneRef.current = true;
    setSpotifyLink({ state: "linking", msg: "Linking Spotify…" });
    SpotifyService.setAuthTokens(tokens);
    (async () => {
      try {
        await SpotifyService.completeLink(code);
        try {
          await sonicRef.current?.controller?.refreshStatus?.();
        } catch {}
        setSpotifyLink({ state: "linked", msg: "Spotify linked ✓" });
        setTimeout(() => {
          window.history.replaceState({}, "", "/");
          setSpotifyLink(null);
        }, 1600);
      } catch (e: any) {
        console.error("[SergaOS] Spotify link failed", e);
        setSpotifyLink({
          state: "error",
          msg: `Link failed: ${e?.message || e}`,
        });
        window.history.replaceState({}, "", "/");
      }
    })();
  }, [isAuthenticated, tokens]);

  // Auth gate: lock/unlock the field and pull live projects on authentication.
  useEffect(() => {
    const world = worldRef.current;
    if (!world) return;
    let cancelled = false;
    tokensRef.current = tokens;

    if (isAuthenticated && tokens) {
      (async () => {
        // Set tokens, load live data + reconcile, then bloom the field in.
        ProjectsService.setAuthTokens(tokens);
        FinanceService.setAuthTokens(tokens);
        FeedService.setAuthTokens(tokens);
        TravelService.setAuthTokens(tokens);
        SpotifyService.setAuthTokens(tokens);
        ChangesService.setAuthTokens(tokens);
        VitalsService.setAuthTokens(tokens);
        CaptureService.setAuthTokens(tokens);
        SystemService.setAuthTokens(tokens);
        // (Spotify OAuth callback is handled by its own dedicated effect below,
        // independent of the world-boot gate, so it can't be silently skipped.)
        if (!liveLoadedRef.current) {
          await reloadProjects();
          await refreshFeed();
          // Establish the change-feed cursor at load time (not at first poll)
          // so edits in the first interval aren't skipped.
          try {
            const hs = await ChangesService.getChanges(null);
            changesCursorRef.current = hs?.now || null;
          } catch (e) {
            console.warn("[SergaOS] change cursor handshake failed", e);
          }
          liveLoadedRef.current = true;
        }
        if (cancelled) return;
        document.body.classList.remove("locked");
        world.unlock();
        trayRef.current?.open();
      })();
    } else {
      document.body.classList.add("locked");
      world.lock();
      liveLoadedRef.current = false;
      changesCursorRef.current = null;
      projectDetailsRef.current = new Map();
    }

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, tokens, reloadProjects, refreshFeed]);

  // Poll the FEED while authenticated — only when the tab is visible, so the
  // Mastodon stream refreshes "while you're looking" without background burn.
  // The server-side TTL means most polls just return the cache cheaply.
  useEffect(() => {
    if (!isAuthenticated) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refreshFeed();
    }, 180000);
    return () => clearInterval(id);
  }, [isAuthenticated, refreshFeed]);

  // Poll the change-feed every minute (visible tab only) so projects, finances
  // and focus stay live without a manual reload. Each poll is a cheap delta:
  // nothing changed → tiny response, no re-render. Also refresh on tab re-focus
  // so returning to a long-idle tab catches up immediately.
  useEffect(() => {
    if (!isAuthenticated) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") pollChanges();
    }, 60000);
    const onVisible = () => {
      if (document.visibilityState === "visible") pollChanges();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAuthenticated, pollChanges]);

  const onAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSubmitting(true);
    try {
      await signIn(creds.user.trim(), creds.pass);
    } catch {
      // error is surfaced via the AuthContext error state below
    } finally {
      setSubmitting(false);
    }
  };

  // Show the card only once the initial Cognito session check has resolved, so
  // a restored session goes straight to the bloom (no login-card flash).
  const showAuth = !isAuthenticated && !isLoading;

  return (
    <div id="serga-os">
      <div id="app">
        <canvas id="scene"></canvas>
        <div id="css3d"></div>

        {/* HUD overlay */}
        <div id="hud">
          <div className="bracket tl"></div>
          <div className="bracket tr"></div>
          <div className="bracket bl"></div>
          <div className="bracket br"></div>

          <div className="topbar">
            <div className="brand">
              <span className="mark">
                SERGA<b>.</b>
              </span>
              <span className="role">Personal Operating System</span>
            </div>
            <div className="sysline">
              <span>
                <span className="dot"></span>SYSTEMS NOMINAL
              </span>
              <span>PRG · 50.07 N</span>
              <span className="clock" id="clock">
                00:00:00
              </span>
            </div>
          </div>

          <div className="crumbs" id="crumbs"></div>

          <div className="hint">
            <span>
              <b>DRAG</b> ORBIT
            </span>
            <span>
              <b>SCROLL</b> ZOOM
            </span>
            <span>
              <b>CLICK</b> CARD
            </span>
            <span>
              <b>ESC</b> BACK
            </span>
          </div>
        </div>

        <button className="backbtn" id="backbtn">
          ◂ BACK
        </button>

        {/* config + shell + lock */}
        <button
          className="fsbtn"
          id="fsbtn"
          title="fullscreen (F)"
          aria-label="toggle fullscreen"
        >
          <svg className="ic-out" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square">
            <path d="M1 5V1h4" /><path d="M9 1h4v4" /><path d="M13 9v4H9" /><path d="M5 13H1V9" />
          </svg>
          <svg className="ic-in" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square">
            <path d="M5 1v4H1" /><path d="M13 5H9V1" /><path d="M9 13V9h4" /><path d="M1 9h4v4" />
          </svg>
        </button>
        <button className="lockbtn" onClick={() => signOut()} title="lock session">
          <span className="lk"></span>LOCK
        </button>
        <button
          className="shellbtn"
          id="shellbtn"
          title="open the editor console (` key)"
        >
          <span className="chev">&#x203A;_</span>SHELL
        </button>
        <button className="cfgbtn" id="cfgbtn">
          <span className="gear"></span>CONFIG
        </button>
        <div className="cfg" id="cfg">
          <h3>SYSTEM CONFIG</h3>
          <div className="crow">
            <label>ACCENT CHANNEL</label>
            <div className="swatches" id="cfg-accent">
              <div className="swatch" data-v="teal" style={{ background: "#2fe6d4" }}></div>
              <div className="swatch" data-v="cyan" style={{ background: "#49b6ff" }}></div>
              <div className="swatch" data-v="amber" style={{ background: "#ffb454" }}></div>
              <div className="swatch" data-v="violet" style={{ background: "#b07cff" }}></div>
            </div>
          </div>
          <div className="crow">
            <label>SCANLINES</label>
            <div className="seg" id="cfg-scan">
              <button data-v="true">ON</button>
              <button data-v="false">OFF</button>
            </div>
          </div>
          <div className="crow">
            <label>STARFIELD DENSITY</label>
            <div className="seg" id="cfg-stars">
              <button data-v="true">DENSE</button>
              <button data-v="false">SPARSE</button>
            </div>
          </div>
          <div className="crow">
            <label>IDLE DRIFT</label>
            <div className="seg" id="cfg-motion">
              <button data-v="true">ON</button>
              <button data-v="false">OFF</button>
            </div>
          </div>
          <div className="crow">
            <label>FIELD LAYOUT</label>
            <div className="seg" id="cfg-layout">
              <button data-act="reset">RESET POSITIONS</button>
            </div>
          </div>
          <div className="crow">
            <label>CLOUD</label>
            <a
              className="cfg-link"
              href="https://cloud.serga.ba"
              target="_blank"
              rel="noreferrer"
            >
              OPEN <span className="cfg-link-arrow">&#x25B8;</span>
            </a>
          </div>
        </div>

        {/* fx layer */}
        <div id="fx"></div>

        {/* Spotify link status toast (OAuth callback) */}
        {spotifyLink && (
          <div className={`spotify-toast ${spotifyLink.state}`}>
            <span className="st-dot"></span>
            {spotifyLink.msg}
          </div>
        )}

        {/* FEED tray — agent-curated FOCUS + Mastodon stream; opens on login,
            collapses to an edge handle, auto-hides off the overview. Populated
            imperatively by serga-os/tray.js from the live /feed payload. */}
        <aside id="tray" className="tray">
          <button className="tray-handle" id="tray-handle" title="open feed">
            <span className="th-rot">FEED</span>
            <span className="th-chev">{"◂"}</span>
          </button>
          <div className="tray-inner">
            <div className="tray-bar">
              <span className="tray-id">
                <span className="tdiamond"></span>FEED
              </span>
              <button className="tray-min" id="tray-min" title="collapse">
                {"▸"}
              </button>
            </div>

            <section className="tsec" id="tsec-focus">
              <div className="tsec-head">
                <span className="tsec-name">TODAY{"’"}S FOCUS</span>
                <span className="tsec-tag">
                  <span id="focus-count">00</span> ITEMS
                </span>
              </div>
              <div className="tsec-sub" id="focus-stamp"></div>
              <div className="focus-list" id="focus-list"></div>
            </section>

            <section className="tsec" id="tsec-threads">
              <div className="tsec-head">
                <span className="tsec-name">THREADS</span>
                <span className="tsec-tag sync-tag">
                  @serge.ba<span className="sync-dot"></span>
                </span>
              </div>
              <div className="threads-list" id="threads-list"></div>
            </section>
          </div>
        </aside>

        {/* SONIC tray — Spotify transport; mirrors the FEED rail, shares the right
            dock (opening one collapses the other). Built imperatively by sonic.js. */}
        <div id="sonic" className="sonic"></div>

        {/* CLOUD portal — ZN-04 blooms the home-node landing (iframe) open in-place
            over the field. main.js owns the src/lifecycle (prefetch/build/teardown). */}
        <div id="cloud-portal">
          <iframe id="cp-iframe" title="CLOUD · home node"></iframe>
          <button id="cp-exit" className="cp-exit" type="button">
            ◂ EXIT NODE
          </button>
        </div>

        {/* auth gate — single login card over the live field, wired to Cognito */}
        <div id="auth" className={showAuth ? "" : "gone"}>
          <div className={`auth-card${error ? " shake" : ""}`}>
            <span className="c-corner tl"></span>
            <span className="c-corner br"></span>
            <div className="auth-head">
              <div className="auth-mark">
                SERGA<b>.</b>
              </div>
              <div className="auth-sub">PERSONAL OPERATING SYSTEM</div>
            </div>
            <div className="auth-status">SECURE CHANNEL ESTABLISHED</div>
            <form className="auth-form" onSubmit={onAuthSubmit} autoComplete="off" noValidate>
              <label className="auth-field">
                <span className="af-label">OPERATOR</span>
                <input
                  value={creds.user}
                  onChange={(e) => setCreds((c) => ({ ...c, user: e.target.value }))}
                  type="text"
                  placeholder="username"
                  autoComplete="username"
                  spellCheck={false}
                  disabled={submitting}
                />
              </label>
              <label className="auth-field">
                <span className="af-label">PASSKEY</span>
                <input
                  value={creds.pass}
                  onChange={(e) => setCreds((c) => ({ ...c, pass: e.target.value }))}
                  type="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  disabled={submitting}
                />
              </label>
              <div className="auth-err">{error ? error.message : ""}</div>
              <button type="submit" className="auth-go" disabled={submitting}>
                {submitting ? "AUTHENTICATING…" : "AUTHENTICATE"}{" "}
                <span className="go-chev">&#x25B8;</span>
              </button>
            </form>
            <div className="auth-foot">
              <span>SRG-OS v4.2</span>
              <span>PRG · 50.07 N</span>
            </div>
          </div>
        </div>

        {/* boot */}
        <div id="boot">
          <div className="bootbox">
            <div className="bootmark">
              SERGA<b>.</b>
            </div>
            <div className="bootlog">INITIALISING…</div>
            <div className="bootbar">
              <i></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SergaOS;
